import React, { useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useConfirm } from '../../../shared/components/ConfirmDialog/useConfirm';
import { useEmploye, useDesactiverEmploye, useReactiverEmploye, usePrimesEmploye, useAttribuerPrime, useSuspendreAttribution, useOppositionsEmploye, useCreerOpposition, useDesactiverOpposition } from '../hooks/useEmployes';
import { useQuery } from '@tanstack/react-query';
import { configurationApi } from '../../configuration/api/configuration.api';
import { useBulletinsParPeriode } from '../../bulletins/hooks/useBulletins';
import { PeriodeSelector } from '../../bulletins/components/PeriodeSelector';
import { employesApi } from '../api/employes.api';
import { useContributionsParEmploye } from '../../contributionsProjet/presentation/hooks/useContributionsProjet';

const MOIS_NOMS = [
  'Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin',
  'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre',
];

/** Génère une couleur Tailwind déterministe depuis le nom de l'employé */
function couleurAvatar(nom: string): string {
  const palette = [
    'bg-indigo-500', 'bg-violet-500', 'bg-blue-500', 'bg-sky-500',
    'bg-emerald-500', 'bg-teal-500', 'bg-amber-500', 'bg-rose-500',
  ];
  let hash = 0;
  for (let i = 0; i < nom.length; i++) {
    hash = nom.charCodeAt(i) + ((hash << 5) - hash);
  }
  return palette[Math.abs(hash) % palette.length];
}

/** Calcule l'ancienneté à partir de la date d'embauche */
function anciennete(dateEmbauche: string): string {
  const debut = new Date(dateEmbauche);
  const now = new Date();
  const moisTotal =
    (now.getFullYear() - debut.getFullYear()) * 12 +
    (now.getMonth() - debut.getMonth());
  const ans = Math.floor(moisTotal / 12);
  const m = moisTotal % 12;
  if (ans === 0) return `${m} mois`;
  return `${ans} an${ans > 1 ? 's' : ''}${m > 0 ? ` ${m} mois` : ''}`;
}

const f = (n: number) => n.toLocaleString('fr-FR') + ' F';

const FREQUENCE_LABELS: Record<string, string> = {
  MENSUEL: 'Mensuel', TRIMESTRIEL: 'Trimestriel',
  SEMESTRIEL: 'Semestriel', ANNUEL: 'Annuel', PONCTUEL: 'Ponctuel',
};

const FORM_PRIME_VIDE = {
  idPrimeTemplate: '',
  montantPersonnalise: '',
  tauxPersonnalise: '',
  frequence: 'MENSUEL',
  dateDebutAttribution: new Date().toISOString().split('T')[0],
};

const TYPES_OPPOSITION = [
  { value: 'SAISIE_ARRET', label: 'Saisie-arrêt' },
  { value: 'AVANCE', label: 'Avance sur salaire' },
  { value: 'PRET', label: 'Prêt employeur' },
  { value: 'PENSION_ALIMENTAIRE', label: 'Pension alimentaire' },
  { value: 'AUTRE', label: 'Autre' },
];

const FORM_OPPOSITION_VIDE = {
  type: 'SAISIE_ARRET',
  libelle: '',
  montant: '',
  reference: '',
  dateDebut: new Date().toISOString().split('T')[0],
  dateFin: '',
};

function ContributionsEmployeSection({ idEmploye }: { idEmploye: string | undefined }) {
  const { data: contributions = [] } = useContributionsParEmploye(idEmploye);
  const f = (n: number) => n.toLocaleString('fr-FR') + ' F';
  return (
    <div className="bg-surface border border-slate-200 rounded-2xl overflow-hidden shadow-sm">
      <div className="px-6 py-4 border-b border-slate-100 bg-slate-50 flex items-center justify-between">
        <h2 className="text-xs font-semibold text-slate-500 uppercase tracking-widest">Contributions projets</h2>
        <Link
          to={`/paie/contributions/employe/${idEmploye}`}
          className="text-xs text-primary-text hover:text-primary font-medium"
        >
          Gérer →
        </Link>
      </div>
      {contributions.length === 0 ? (
        <div className="px-6 py-4 text-sm text-slate-400">Aucune contribution enregistrée</div>
      ) : (
        <div className="divide-y divide-slate-100">
          {contributions.map(c => (
            <div key={c.idContribution} className="px-6 py-3 flex items-center justify-between">
              <span className="text-sm text-content">{c.nomProjet ?? '—'}</span>
              <span className="text-sm font-semibold font-mono tabular-nums text-primary-text">{f(c.montant)}</span>
            </div>
          ))}
          <div className="px-6 py-3 flex items-center justify-between bg-slate-50">
            <span className="text-xs font-semibold text-slate-500 uppercase">Total</span>
            <span className="text-sm font-bold font-mono tabular-nums text-content">{f(contributions.reduce((s, c) => s + c.montant, 0))}</span>
          </div>
        </div>
      )}
    </div>
  );
}

export function EmployeDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const now = new Date();
  const [mois, setMois] = useState(now.getMonth() + 1);
  const [annee, setAnnee] = useState(now.getFullYear());
  const [showFormPrime, setShowFormPrime] = useState(false);
  const [formPrime, setFormPrime] = useState(FORM_PRIME_VIDE);
  const [showFormOpposition, setShowFormOpposition] = useState(false);
  const [formOpposition, setFormOpposition] = useState(FORM_OPPOSITION_VIDE);
  const [showTerminees, setShowTerminees] = useState(false);
  const [idUtilisateurLier, setIdUtilisateurLier] = useState('');
  const [lierEnCours, setLierEnCours] = useState(false);
  const [lierMessage, setLierMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const { data: employe, isLoading, isError, refetch: refetchEmploye } = useEmploye(id);
  const { data: bulletins = [] } = useBulletinsParPeriode(annee, mois);
  const bulletinsEmploye = bulletins.filter(b => b.idEmploye === id);
  const { data: primes = [] } = usePrimesEmploye(id);
  const { data: templates = [] } = useQuery({ queryKey: ['prime-templates'], queryFn: configurationApi.listerTemplatesPrimes });
  const attribuer = useAttribuerPrime(id ?? '');
  const suspendre = useSuspendreAttribution(id ?? '');
  const { data: oppositions = [] } = useOppositionsEmploye(id);
  const creerOpposition = useCreerOpposition(id ?? '');
  const desactiverOpp = useDesactiverOpposition(id ?? '');

  const desactiver = useDesactiverEmploye();
  const reactiver = useReactiverEmploye();
  const enCours = desactiver.isPending || reactiver.isPending;
  const confirm = useConfirm();

  const handleToggleStatut = async () => {
    if (!id || !employe) return;
    if (employe.actif) {
      const ok = await confirm({
        title: `Désactiver ${employe.nom} ${employe.prenoms} ?`,
        message: 'Cette action peut être annulée à tout moment en réactivant l\'employé.',
        variant: 'warning',
        labelConfirm: 'Désactiver',
      });
      if (!ok) return;
      await desactiver.mutateAsync(id);
    } else {
      const ok = await confirm({
        title: `Réactiver ${employe.nom} ${employe.prenoms} ?`,
        variant: 'info',
        labelConfirm: 'Réactiver',
      });
      if (!ok) return;
      await reactiver.mutateAsync(id);
    }
  };

  /* ── Skeleton chargement ── */
  if (isLoading) {
    return (
      <div className="p-8 max-w-5xl mx-auto">
        <div className="animate-pulse space-y-5">
          <div className="h-4 bg-slate-200 rounded w-36" />
          <div className="h-28 bg-slate-200 rounded-2xl" />
          <div className="grid grid-cols-3 gap-6">
            <div className="col-span-2 h-60 bg-slate-200 rounded-2xl" />
            <div className="h-60 bg-slate-200 rounded-2xl" />
          </div>
        </div>
      </div>
    );
  }

  /* ── Erreur / introuvable ── */
  if (isError || !employe) {
    return (
      <div className="p-8 max-w-5xl mx-auto">
        <div className="bg-red-50 border border-red-200 rounded-2xl p-8 text-center">
          <p className="text-red-700 font-semibold text-lg">Employé introuvable</p>
          <p className="text-red-400 text-sm mt-2">
            Cet employé n'existe pas ou vous n'y avez pas accès.
          </p>
          <button
            onClick={() => navigate('/paie/employes')}
            className="mt-5 px-5 py-2.5 bg-red-600 text-white rounded-xl text-sm font-medium hover:bg-red-700 transition-colors"
          >
            Retour à la liste
          </button>
        </div>
      </div>
    );
  }

  const initiales = `${employe.nom.charAt(0)}${employe.prenoms.charAt(0)}`.toUpperCase();

  return (
    <div className="p-6 max-w-5xl mx-auto">
      {/* Bouton retour */}
      <button
        onClick={() => navigate('/paie/employes')}
        className="flex items-center gap-1 text-slate-400 hover:text-slate-600 text-sm mb-6 transition-colors"
      >
        ← Retour aux employés
      </button>

      {/* ── Carte identité RH ── */}
      <div className="bg-surface border border-slate-200 rounded-2xl p-6 shadow-sm mb-6">
        <div className="flex items-start justify-between gap-4">
          {/* Avatar + infos */}
          <div className="flex items-center gap-5">
            <div
              className={`w-16 h-16 rounded-2xl ${couleurAvatar(employe.nom)} flex items-center justify-center text-white text-xl font-bold flex-shrink-0 shadow-sm`}
            >
              {initiales}
            </div>
            <div>
              <h1 className="text-2xl font-bold text-slate-900">
                {employe.nom} {employe.prenoms}
              </h1>
              <p className="text-slate-500 text-base mt-0.5">{employe.fonction}</p>
              <div className="flex items-center gap-3 mt-2 flex-wrap">
                <span
                  className={`px-3 py-1 rounded-full text-xs font-semibold border ${
                    employe.actif
                      ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                      : 'bg-slate-100 text-slate-500 border-slate-200'
                  }`}
                >
                  {employe.actif ? '● Actif' : '○ Inactif'}
                </span>
                {employe.numeroMatricule && (
                  <span className="text-xs text-slate-400 bg-slate-100 px-2 py-0.5 rounded-full">
                    Matricule {employe.numeroMatricule}
                  </span>
                )}
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="flex gap-2 flex-shrink-0">
            <Link
              to={`/paie/employes/${id}/modifier`}
              className="px-4 py-2 border border-slate-200 text-slate-600 rounded-xl text-sm font-medium hover:bg-slate-50 transition-colors"
            >
              Modifier
            </Link>
            <button
              onClick={handleToggleStatut}
              disabled={enCours}
              className={`px-4 py-2 rounded-xl text-sm font-medium transition-colors disabled:opacity-60 border ${
                employe.actif
                  ? 'bg-rose-50 text-rose-600 border-rose-200 hover:bg-rose-100'
                  : 'bg-emerald-50 text-emerald-600 border-emerald-200 hover:bg-emerald-100'
              }`}
            >
              {enCours ? '…' : employe.actif ? 'Désactiver' : 'Réactiver'}
            </button>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* ── Colonne principale ── */}
        <div className="lg:col-span-2 space-y-6">
          {/* Informations administratives */}
          <div className="bg-surface border border-slate-200 rounded-2xl overflow-hidden shadow-sm">
            <div className="px-6 py-4 border-b border-slate-100 bg-slate-50">
              <h2 className="text-xs font-semibold text-slate-500 uppercase tracking-widest">
                Informations administratives
              </h2>
            </div>
            <div className="p-6 grid grid-cols-2 gap-x-10 gap-y-5">
              {[
                { label: 'N° CNSS', value: employe.numeroCnss || '—' },
                { label: 'IFU', value: employe.ifu || '—' },
                { label: 'NPI', value: employe.npi || '—' },
                { label: 'Lieu de travail', value: employe.lieuTravail || '—' },
                { label: 'Téléphone', value: employe.telephone || '—' },
                {
                  label: "Date d'embauche",
                  value: new Date(employe.dateEmbauche).toLocaleDateString('fr-FR', {
                    day: '2-digit',
                    month: 'long',
                    year: 'numeric',
                  }),
                },
                { label: 'Ancienneté', value: anciennete(employe.dateEmbauche) },
              ].map(({ label, value }) => (
                <div key={label}>
                  <p className="text-xs text-slate-400 uppercase tracking-wide mb-1">{label}</p>
                  <p className="text-sm font-medium text-slate-800">{value}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Historique des bulletins */}
          <div className="bg-surface border border-slate-200 rounded-2xl overflow-hidden shadow-sm">
            <div className="px-6 py-4 border-b border-slate-100 bg-slate-50 flex items-center justify-between flex-wrap gap-3">
              <h2 className="text-xs font-semibold text-slate-500 uppercase tracking-widest">
                Historique des bulletins
              </h2>
              <PeriodeSelector
                mois={mois}
                annee={annee}
                onChange={(m, a) => { setMois(m); setAnnee(a); }}
              />
            </div>

            {bulletinsEmploye.length === 0 ? (
              <div className="py-10 text-center text-slate-400 text-sm">
                Aucun bulletin pour {MOIS_NOMS[mois - 1]} {annee}.
              </div>
            ) : (
              <table className="w-full">
                <thead className="bg-slate-50">
                  <tr>
                    {['Période', 'Brut', 'Net', 'À payer', 'Statut', ''].map(h => (
                      <th
                        key={h}
                        className="px-5 py-3 text-left text-xs font-medium text-slate-400 uppercase tracking-wider"
                      >
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {bulletinsEmploye.map(b => (
                    <tr key={b.idBulletin} className="hover:bg-slate-50/50 transition-colors">
                      <td className="px-5 py-3 text-sm text-slate-600">
                        {MOIS_NOMS[b.mois - 1]} {b.annee}
                      </td>
                      <td className="px-5 py-3 text-sm font-mono tabular-nums text-slate-700">
                        {f(b.salaireBrut)}
                      </td>
                      <td className="px-5 py-3 text-sm font-mono tabular-nums text-slate-700">
                        {f(b.salaireNet)}
                      </td>
                      <td className="px-5 py-3 text-sm font-mono tabular-nums font-semibold text-primary-text">
                        {f(b.netAPayer)}
                      </td>
                      <td className="px-5 py-3">
                        <span
                          className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                            b.statut === 'VALIDE'
                              ? 'bg-emerald-100 text-emerald-700'
                              : 'bg-amber-100 text-amber-700'
                          }`}
                        >
                          {b.statut}
                        </span>
                      </td>
                      <td className="px-5 py-3">
                        <Link
                          to={`/paie/bulletins/${b.idBulletin}`}
                          className="text-xs text-primary-text hover:text-primary font-medium transition-colors"
                        >
                          Voir →
                        </Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
          {/* Section Primes attribuées */}
          <div className="bg-surface border border-slate-200 rounded-2xl overflow-hidden shadow-sm">
            <div className="px-6 py-4 border-b border-slate-100 bg-slate-50 flex items-center justify-between">
              <h2 className="text-xs font-semibold text-slate-500 uppercase tracking-widest">
                Primes attribuées
              </h2>
              <button
                onClick={() => { setFormPrime(FORM_PRIME_VIDE); setShowFormPrime(v => !v); }}
                className="text-xs text-primary-text hover:text-primary font-medium"
              >
                {showFormPrime ? 'Annuler' : '+ Attribuer une prime'}
              </button>
            </div>

            {showFormPrime && (
              <div className="p-5 border-b border-slate-100 bg-primary-light/30">
                <form
                  onSubmit={e => {
                    e.preventDefault();
                    attribuer.mutate({
                      idPrimeTemplate: formPrime.idPrimeTemplate,
                      montantPersonnalise: formPrime.montantPersonnalise ? Number(formPrime.montantPersonnalise) : undefined,
                      frequence: formPrime.frequence,
                      dateDebutAttribution: formPrime.dateDebutAttribution,
                    }, { onSuccess: () => setShowFormPrime(false) });
                  }}
                  className="space-y-3"
                >
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-medium text-slate-600 mb-1">Modèle de prime *</label>
                      <select
                        required
                        value={formPrime.idPrimeTemplate}
                        onChange={e => setFormPrime(f => ({ ...f, idPrimeTemplate: e.target.value }))}
                        className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                      >
                        <option value="">Choisir...</option>
                        {(templates as any[]).map((t: any) => (
                          <option key={t.idPrimeTemplate} value={t.idPrimeTemplate}>{t.nom}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-slate-600 mb-1">Fréquence</label>
                      <select
                        value={formPrime.frequence}
                        onChange={e => setFormPrime(f => ({ ...f, frequence: e.target.value }))}
                        className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                      >
                        {Object.entries(FREQUENCE_LABELS).map(([v, l]) => (
                          <option key={v} value={v}>{l}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-medium text-slate-600 mb-1">Montant personnalisé (F, optionnel)</label>
                      <input
                        type="number"
                        value={formPrime.montantPersonnalise}
                        onChange={e => setFormPrime(f => ({ ...f, montantPersonnalise: e.target.value }))}
                        placeholder="Laissez vide pour utiliser le suggéré"
                        className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-slate-600 mb-1">Date début *</label>
                      <input
                        type="date"
                        required
                        value={formPrime.dateDebutAttribution}
                        onChange={e => setFormPrime(f => ({ ...f, dateDebutAttribution: e.target.value }))}
                        className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                      />
                    </div>
                  </div>
                  <button
                    type="submit"
                    disabled={attribuer.isPending}
                    className="px-4 py-2 bg-primary text-content-inverted rounded-lg text-sm font-medium hover:bg-primary-hover disabled:opacity-50 transition-colors"
                  >
                    {attribuer.isPending ? 'Attribution...' : 'Attribuer'}
                  </button>
                </form>
              </div>
            )}

            {(primes as any[]).length === 0 && !showFormPrime ? (
              <div className="py-8 text-center text-slate-400 text-sm">
                Aucune prime attribuée.
              </div>
            ) : (
              <div className="divide-y divide-slate-100">
                {(primes as any[]).map((p: any) => (
                  <div key={p.idPrimeAttribution} className={`px-6 py-3 flex items-center justify-between ${!p.actifPourEmploye ? 'opacity-50' : ''}`}>
                    <div>
                      <p className="text-sm font-medium text-slate-800">{p.primeTemplate?.nom}</p>
                      <p className="text-xs text-slate-400 mt-0.5">
                        {FREQUENCE_LABELS[p.frequence]} ·{' '}
                        {p.montantPersonnalise != null ? f(Number(p.montantPersonnalise)) : 'Montant suggéré'}
                        {' · depuis '}
                        {new Date(p.dateDebutAttribution).toLocaleDateString('fr-FR')}
                      </p>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${p.statutAttribution === 'ACTIF' ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-500'}`}>
                        {p.statutAttribution}
                      </span>
                      {p.statutAttribution === 'ACTIF' && (
                        <button
                          onClick={() => suspendre.mutate(p.idPrimeAttribution)}
                          disabled={suspendre.isPending}
                          className="text-xs text-rose-500 hover:text-rose-700 font-medium disabled:opacity-50"
                        >
                          Suspendre
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
          {/* Section Oppositions sur salaire */}
          {(() => {
            const oppActives = (oppositions as any[]).filter((o: any) => o.actif);
            const oppTerminees = (oppositions as any[]).filter((o: any) => !o.actif);
            const totalActif = oppActives.reduce((s: number, o: any) => s + Number(o.montant), 0);
            return (
              <div className="bg-surface border border-slate-200 rounded-2xl overflow-hidden shadow-sm">
                <div className="px-6 py-4 border-b border-slate-100 bg-slate-50 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <h2 className="text-xs font-semibold text-slate-500 uppercase tracking-widest">
                      Oppositions sur salaire
                    </h2>
                    {totalActif > 0 && (
                      <span className="px-2 py-0.5 rounded-full text-xs font-semibold bg-rose-100 text-rose-700">
                        {f(totalActif)} / mois
                      </span>
                    )}
                  </div>
                  <button
                    onClick={() => { setFormOpposition(FORM_OPPOSITION_VIDE); setShowFormOpposition(v => !v); }}
                    className="text-xs text-primary-text hover:text-primary font-medium"
                  >
                    {showFormOpposition ? 'Annuler' : '+ Ajouter une opposition'}
                  </button>
                </div>

                {showFormOpposition && (
                  <div className="p-5 border-b border-slate-100 bg-rose-50/20">
                    <form
                      onSubmit={e => {
                        e.preventDefault();
                        creerOpposition.mutate({
                          type: formOpposition.type,
                          libelle: formOpposition.libelle,
                          montant: Number(formOpposition.montant),
                          reference: formOpposition.reference,
                          dateDebut: formOpposition.dateDebut,
                          dateFin: formOpposition.dateFin || undefined,
                        }, { onSuccess: () => setShowFormOpposition(false) });
                      }}
                      className="space-y-3"
                    >
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <label className="block text-xs font-medium text-slate-600 mb-1">Type *</label>
                          <select
                            required
                            value={formOpposition.type}
                            onChange={e => setFormOpposition(f => ({ ...f, type: e.target.value }))}
                            className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-rose-400"
                          >
                            {TYPES_OPPOSITION.map(t => (
                              <option key={t.value} value={t.value}>{t.label}</option>
                            ))}
                          </select>
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-slate-600 mb-1">Montant mensuel (F) *</label>
                          <input
                            type="number"
                            required
                            min={1}
                            value={formOpposition.montant}
                            onChange={e => setFormOpposition(f => ({ ...f, montant: e.target.value }))}
                            className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-rose-400"
                          />
                        </div>
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-slate-600 mb-1">Libellé *</label>
                        <input
                          type="text"
                          required
                          value={formOpposition.libelle}
                          onChange={e => setFormOpposition(f => ({ ...f, libelle: e.target.value }))}
                          className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-rose-400"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-slate-600 mb-1">Référence (n° jugement, décision…) *</label>
                        <input
                          type="text"
                          required
                          value={formOpposition.reference}
                          onChange={e => setFormOpposition(f => ({ ...f, reference: e.target.value }))}
                          className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-rose-400"
                        />
                      </div>
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <label className="block text-xs font-medium text-slate-600 mb-1">Date début *</label>
                          <input
                            type="date"
                            required
                            value={formOpposition.dateDebut}
                            onChange={e => setFormOpposition(f => ({ ...f, dateDebut: e.target.value }))}
                            className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-rose-400"
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-slate-600 mb-1">Date fin (optionnelle)</label>
                          <input
                            type="date"
                            value={formOpposition.dateFin}
                            onChange={e => setFormOpposition(f => ({ ...f, dateFin: e.target.value }))}
                            className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-rose-400"
                          />
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <button
                          type="submit"
                          disabled={creerOpposition.isPending}
                          className="px-4 py-2 bg-rose-600 text-white rounded-lg text-sm font-medium hover:bg-rose-700 disabled:opacity-50 transition-colors"
                        >
                          {creerOpposition.isPending ? 'Enregistrement...' : 'Enregistrer'}
                        </button>
                        <button
                          type="button"
                          onClick={() => setShowFormOpposition(false)}
                          className="px-4 py-2 border border-slate-200 text-slate-600 rounded-lg text-sm font-medium hover:bg-slate-50 transition-colors"
                        >
                          Annuler
                        </button>
                      </div>
                    </form>
                  </div>
                )}

                {oppActives.length === 0 && !showFormOpposition ? (
                  <div className="py-8 text-center text-slate-400 text-sm">
                    Aucune opposition active.
                  </div>
                ) : (
                  <div className="divide-y divide-slate-100">
                    {oppActives.map((o: any) => (
                      <div key={o.idOpposition} className="px-6 py-3 flex items-center justify-between gap-4">
                        <div className="flex items-center gap-3 min-w-0">
                          <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-700 flex-shrink-0">
                            {TYPES_OPPOSITION.find(t => t.value === o.type)?.label ?? o.type}
                          </span>
                          <div className="min-w-0">
                            <p className="text-sm font-medium text-slate-800 truncate">{o.libelle}</p>
                            <p className="text-xs text-slate-400 mt-0.5">
                              Réf: {o.reference} · depuis {new Date(o.dateDebut).toLocaleDateString('fr-FR')}
                              {o.dateFin ? ` → ${new Date(o.dateFin).toLocaleDateString('fr-FR')}` : ''}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-3 flex-shrink-0">
                          <span className="text-sm font-mono font-semibold text-rose-600">
                            -{f(Number(o.montant))}
                          </span>
                          <button
                            onClick={async () => {
                              const ok = await confirm({
                                title: 'Désactiver cette opposition ?',
                                message: `${o.libelle} — ${f(Number(o.montant))}`,
                                variant: 'danger',
                                labelConfirm: 'Désactiver',
                              });
                              if (ok) desactiverOpp.mutate(o.idOpposition);
                            }}
                            disabled={desactiverOpp.isPending}
                            className="text-xs text-rose-500 hover:text-rose-700 font-medium disabled:opacity-50"
                          >
                            Désactiver
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {oppTerminees.length > 0 && (
                  <div className="border-t border-slate-100">
                    <button
                      onClick={() => setShowTerminees(v => !v)}
                      className="w-full px-6 py-3 text-xs text-slate-400 hover:text-slate-600 text-left flex items-center gap-2 transition-colors"
                    >
                      {showTerminees ? '▲' : '▼'} {oppTerminees.length} opposition{oppTerminees.length > 1 ? 's' : ''} terminée{oppTerminees.length > 1 ? 's' : ''}
                    </button>
                    {showTerminees && (
                      <div className="divide-y divide-slate-100 opacity-50">
                        {oppTerminees.map((o: any) => (
                          <div key={o.idOpposition} className="px-6 py-3 flex items-center justify-between gap-4">
                            <div className="flex items-center gap-3 min-w-0">
                              <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-slate-100 text-slate-500 flex-shrink-0">
                                {TYPES_OPPOSITION.find(t => t.value === o.type)?.label ?? o.type}
                              </span>
                              <div className="min-w-0">
                                <p className="text-sm font-medium text-slate-700 truncate">{o.libelle}</p>
                                <p className="text-xs text-slate-400 mt-0.5">
                                  Réf: {o.reference} · {new Date(o.dateDebut).toLocaleDateString('fr-FR')}
                                  {o.dateFin ? ` → ${new Date(o.dateFin).toLocaleDateString('fr-FR')}` : ''}
                                </p>
                              </div>
                            </div>
                            <span className="text-sm font-mono text-slate-500 flex-shrink-0">
                              {f(Number(o.montant))}
                            </span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })()}
        </div>

        {/* ── Colonne latérale : rémunération ── */}
        <div className="space-y-4">
          <div className="bg-surface border border-slate-200 rounded-2xl overflow-hidden shadow-sm">
            <div className="px-5 py-4 border-b border-slate-100 bg-slate-50">
              <h2 className="text-xs font-semibold text-slate-500 uppercase tracking-widest">
                Rémunération
              </h2>
            </div>
            <div className="p-5">
              <p className="text-xs text-slate-400 uppercase tracking-wide mb-1">
                Salaire de base
              </p>
              <p className="text-3xl font-bold font-mono tabular-nums text-primary-text">
                {f(employe.salaireBase)}
              </p>

              {employe.valeursPersonnalisees.length > 0 && (
                <div className="mt-5 pt-4 border-t border-slate-100 space-y-3">
                  <p className="text-xs text-slate-400 uppercase tracking-wide">
                    Valeurs personnalisées
                  </p>
                  {employe.valeursPersonnalisees.map(v => (
                    <div key={v.code} className="flex justify-between items-center">
                      <span className="text-xs text-slate-600">{v.nom}</span>
                      <span className="text-xs font-mono font-medium text-slate-800">{v.valeur}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Raccourci génération bulletin */}
          <div className="bg-primary-light border border-primary rounded-2xl p-5">
            <p className="text-xs font-semibold text-primary-text uppercase tracking-widest mb-3">
              Actions rapides
            </p>
            <Link
              to={`/paie/bulletins/generer?idEmploye=${id}`}
              className="block w-full text-center px-4 py-2.5 bg-primary text-content-inverted rounded-xl text-sm font-medium hover:bg-primary-hover transition-colors shadow-sm"
            >
              Générer un bulletin
            </Link>
          </div>

          {/* Contributions au salaire */}
          <ContributionsEmployeSection idEmploye={id} />

          {/* Compte employé (portail salarié) */}
          <div className="bg-surface border border-slate-200 rounded-2xl overflow-hidden shadow-sm">
            <div className="px-5 py-4 border-b border-slate-100 bg-slate-50">
              <h2 className="text-xs font-semibold text-slate-500 uppercase tracking-widest">
                Compte employé
              </h2>
            </div>
            <div className="p-5">
              {(employe as any).compteUtilisateur ? (
                <div>
                  <p className="text-xs text-slate-500 mb-1">Compte lié :</p>
                  <p className="text-sm font-medium text-slate-800 break-all">
                    {(employe as any).compteUtilisateur.email ?? (employe as any).compteUtilisateur.idUtilisateur}
                  </p>
                  <p className="text-xs text-emerald-600 mt-1">Portail salarié activé</p>
                </div>
              ) : (
                <div className="space-y-3">
                  <p className="text-xs text-slate-500">
                    Aucun compte utilisateur lié. Saisissez l'ID d'un utilisateur avec le rôle "employe".
                  </p>
                  {lierMessage && (
                    <div className={`text-xs px-3 py-2 rounded-lg ${lierMessage.type === 'success' ? 'bg-emerald-50 text-emerald-700' : 'bg-red-50 text-red-700'}`}>
                      {lierMessage.text}
                    </div>
                  )}
                  <input
                    type="text"
                    placeholder="ID utilisateur"
                    value={idUtilisateurLier}
                    onChange={e => setIdUtilisateurLier(e.target.value)}
                    className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                  />
                  <button
                    disabled={lierEnCours || !idUtilisateurLier.trim()}
                    onClick={async () => {
                      if (!id || !idUtilisateurLier.trim()) return;
                      setLierEnCours(true);
                      setLierMessage(null);
                      try {
                        await employesApi.lierCompte(id, idUtilisateurLier.trim());
                        setLierMessage({ type: 'success', text: 'Compte lié avec succès' });
                        setIdUtilisateurLier('');
                        refetchEmploye();
                      } catch (err: any) {
                        const msg = err?.response?.data?.message ?? 'Erreur lors de la liaison';
                        setLierMessage({ type: 'error', text: msg });
                      } finally {
                        setLierEnCours(false);
                      }
                    }}
                    className="w-full px-4 py-2 bg-slate-700 text-white rounded-lg text-sm font-medium hover:bg-slate-800 disabled:opacity-50 transition-colors"
                  >
                    {lierEnCours ? 'Liaison...' : 'Lier ce compte'}
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
